/*
 * ARTYHC · Tu pared te ha escrito
 * <pared-chat>   chat con guion de la sección sections/pared-chat.liquid
 * <pared-medida> asistente de medidas de blocks/pared-medida.liquid
 * <pared-toasts> reacciones de la pared al añadir al carrito (snippets/pared-toasts.liquid)
 *
 * Todo el contenido llega pintado desde Liquid; aquí solo se anima y se responde.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Espera de guion: con "reducir movimiento" no hay esperas. */
const beat = (ms) => new Promise((resolve) => setTimeout(resolve, reduceMotion ? 0 : ms));
/** Espera real (para tiempos de red), no depende de la animación. */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pad = (n) => String(n).padStart(2, '0');
const nowHHMM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Duración de los tres puntos según lo largo que sea el mensaje. */
const typingTime = (text) => Math.min(1500, 450 + String(text || '').trim().length * 14);

const session = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* almacenamiento bloqueado: se anima en cada visita */
    }
  },
};

function typingDots() {
  const el = document.createElement('div');
  el.className = 'pared__typing';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = '<i></i><i></i><i></i>';
  return el;
}

/**
 * Crea una burbuja nueva.
 * @param {'in'|'out'} side
 * @param {string|Node} content - HTML de confianza (viene de los ajustes del tema) o un nodo.
 * @param {string} [from]
 */
function bubble(side, content, from) {
  const el = document.createElement('div');
  el.className = `pared__m pared__m--${side} pared__new`;
  if (from) {
    const f = document.createElement('span');
    f.className = 'pared__from';
    f.textContent = from;
    el.append(f);
  }
  if (typeof content === 'string') el.insertAdjacentHTML('beforeend', content);
  else el.append(content);
  return el;
}

function textBubble(side, text, from) {
  const el = bubble(side, '', from);
  el.append(document.createTextNode(text));
  return el;
}

/**
 * Inserta una burbuja de la pared con sus tres puntos delante.
 * @param {Element} after - nodo tras el que se inserta
 * @param {Element} node
 * @returns {Promise<Element>}
 */
async function sayAfter(after, node) {
  const chat = after.closest('pared-chat');
  const dots = typingDots();
  dots.classList.add('is-on');
  after.after(dots);
  chat?.follow?.(dots);
  chat?.setTyping?.(true);
  await beat(typingTime(node.textContent));
  dots.replaceWith(node);
  chat?.setTyping?.(false);
  chat?.follow?.(node);
  return node;
}

function copyCode(button) {
  const code = button.dataset.copy || button.textContent.trim();
  const done = () => {
    const original = button.textContent;
    button.textContent = 'copiado ✓';
    setTimeout(() => (button.textContent = original), 1600);
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(code).then(done, () => {});
  }
}

/* ------------------------------------------------------------------ */
/* <pared-chat>                                                        */
/* ------------------------------------------------------------------ */

class ParedChat extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.abort = new AbortController();
    this.log = this.querySelector('.pared__log');
    if (!this.log) return;

    this.querySelectorAll('[data-pared-hora]').forEach((el) => (el.textContent = nowHHMM()));

    // Piezas que solo tienen sentido con JavaScript
    this.querySelectorAll('[data-js-only]').forEach((el) => (el.hidden = false));

    // Guion de día o de noche con la hora del visitante
    const hour = new Date().getHours();
    const from = Number(this.dataset.nightFrom || 21);
    const to = Number(this.dataset.nightTo || 7);
    const phase = hour >= from || hour < to ? 'night' : 'day';
    this.querySelectorAll('[data-phase]').forEach((el) => (el.hidden = el.dataset.phase !== phase));

    // Un producto distinto en cada visita
    this.querySelectorAll('[data-pared-picks]').forEach((wrap) => {
      const picks = [...wrap.querySelectorAll('[data-pared-pick]')];
      if (picks.length < 2) return;
      const chosen = Math.floor(Math.random() * picks.length);
      picks.forEach((a, i) => (a.hidden = i !== chosen));
    });

    this.querySelectorAll('[data-kind="replies"]').forEach((el) => this.setupReplies(el));
    this.querySelectorAll('form[data-pared-mail]').forEach((el) => this.setupMail(el));
    if (this._paredPending) {
      const pending = this._paredPending;
      this._paredPending = null;
      queueMicrotask(() => pending.requestSubmit());
    }
    this.querySelectorAll('[data-pared-photo]').forEach((el) => this.setupPhoto(el));
    this.addEventListener(
      'click',
      (event) => {
        const code = event.target.closest?.('.pared__code');
        if (code) copyCode(code);
      },
      { signal: this.abort.signal }
    );

    this.items = [...this.log.children].filter((el) => el.classList.contains('pared__item') && !el.hidden);
    this.setupSplit();

    const canAnimate =
      this.dataset.animate === '1' &&
      !reduceMotion &&
      !session.get(this.dataset.key) &&
      'IntersectionObserver' in window &&
      !document.hidden &&
      !new URLSearchParams(window.location.search).has('customer_posted');
    if (!canAnimate) {
      this.classList.remove('is-waiting');
      this.querySelector('[data-pared-boot]')?.remove();
      return;
    }

    this.classList.add('is-waiting');
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.play();
      },
      // El chat puede ser más alto que la pantalla: basta con que asome por abajo
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    );
    this.observer.observe(this);
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.finish();
      },
      { signal: this.abort.signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.stopped = true;
  }

  /**
   * Ordenador (990 px o más): la conversación a la izquierda y lo visual (producto, fotos y
   * colecciones) en un escaparate fijo a la derecha. En móvil y tablet todo vuelve a su sitio.
   */
  setupSplit() {
    const media = [...this.log.children].filter((el) =>
      el.matches('.pared__picks, .pared__cats, .pared__pic.pared__item')
    );
    if (!media.length || this.classList.contains('pared--window')) return;
    const query = window.matchMedia('(min-width: 990px)');
    const apply = () => {
      if (query.matches && !this.split) {
        const split = document.createElement('div');
        split.className = 'pared__split';
        const side = document.createElement('div');
        side.className = 'pared__media';
        this.log.before(split);
        split.append(this.log, side);
        for (const el of media) {
          el._paredHome = document.createComment('pared');
          el.before(el._paredHome);
          side.append(el);
        }
        this.split = split;
        this.classList.add('pared--split');
      } else if (!query.matches && this.split) {
        for (const el of media) {
          el._paredHome?.replaceWith(el);
          el._paredHome = null;
        }
        this.split.replaceWith(this.log);
        this.split = null;
        this.classList.remove('pared--split');
      }
    };
    apply();
    query.addEventListener('change', apply, { signal: this.abort.signal });
  }

  /** En la ventana de escritorio el chat tiene su propio scroll: lo baja hasta el último mensaje. */
  follow(el) {
    const log = this.log;
    if (!log || !el || log.scrollHeight <= log.clientHeight + 2) return;
    const target = el.offsetTop + el.offsetHeight - log.clientHeight + 24;
    if (target > log.scrollTop) log.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** "en línea" pasa a "escribiendo…" mientras salen los tres puntos. */
  setTyping(on) {
    const status = this.querySelector('[data-pared-status]');
    if (!status) return;
    status.textContent = on ? 'escribiendo…' : status.dataset.status;
    status.classList.toggle('is-typing', on);
  }

  /** Enseña todo de golpe (fin del guion, pestaña oculta o desmontaje). */
  finish() {
    this.stopped = true;
    this.observer?.disconnect();
    this.dots?.remove();
    this.querySelector('[data-pared-boot]')?.remove();
    this.setTyping(false);
    this.items?.forEach((el) => el.classList.add('is-shown'));
    this.classList.remove('is-waiting', 'is-playing');
    this.release?.();
  }

  async play() {
    session.set(this.dataset.key, '1');
    this.classList.remove('is-waiting');
    this.classList.add('is-playing');
    // Los puntos que pintó el script de la sección mientras cargaba este archivo
    this.dots = this.querySelector('[data-pared-boot]') || typingDots();
    this.dots.removeAttribute('data-pared-boot');
    await beat(250);

    for (const item of this.items) {
      if (this.stopped) return;
      const kind = item.dataset.kind;
      if (kind === 'in') {
        if (item.parentElement === this.log) this.log.insertBefore(this.dots, item);
        else this.log.append(this.dots);
        this.dots.classList.add('is-on');
        this.setTyping(true);
        this.follow(this.dots);
        await beat(typingTime(item.textContent));
        if (this.stopped) return;
        this.dots.classList.remove('is-on');
        this.setTyping(false);
      }
      item.classList.add('is-shown');
      this.follow(item);
      await beat(kind === 'in' ? 300 : 450);
      if (kind === 'replies' && !item.dataset.answered && item.isConnected) {
        await this.waitForReply(item);
      }
    }
    this.finish();
  }

  /** El guion se para hasta que el cliente contesta (máximo 9 s). */
  waitForReply(item) {
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        item.removeEventListener('pared:answered', done);
        this.release = null;
        resolve();
      };
      const timer = setTimeout(done, 9000);
      this.release = done;
      item.addEventListener('pared:answered', done);
    });
  }

  setupReplies(box) {
    box.addEventListener('click', async (event) => {
      const button = event.target.closest('button[data-i]');
      if (!button || box.dataset.answered) return;
      box.dataset.answered = '1';
      const out = textBubble('out', button.textContent.trim());
      const template = box.querySelector(`template[data-i="${button.dataset.i}"]`);
      const mailTemplate = button.dataset.mail ? box.querySelector('template[data-mail]') : null;
      box.replaceWith(out);
      box.dispatchEvent(new Event('pared:answered'));
      let last = out;
      if (template && template.innerHTML.trim()) {
        last = await sayAfter(out, bubble('in', template.innerHTML));
      }
      if (mailTemplate) {
        // "Quiero un descuento": el formulario del 10 % aparece dentro del chat
        const fragment = mailTemplate.content.cloneNode(true);
        const wrap = fragment.querySelector('.pared__mailwrap');
        if (!wrap) return;
        wrap.classList.add('pared__new');
        last.after(wrap);
        this.setupMail(wrap.querySelector('form'));
        this.follow(wrap);
        wrap.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      }
    });
  }

  setupMail(form) {
    const wrap = form.closest('.pared__mailwrap') || form.parentElement;
    const error = wrap.querySelector('.pared__err');
    const config = form.querySelector('[data-klaviyo-key]')?.dataset || {};

    form.addEventListener('submit', async (event) => {
      // Shopify reenvía a la fuerza los formularios de cliente desde un listener en document
      // (su captcha). Cortamos aquí para que el email se quede en el chat y no recargue la página.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.dataset.sending) return;
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button[type="submit"]');
      const email = input.value.trim();
      const showError = (text) => {
        error.textContent = text;
        error.hidden = false;
      };

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        button.disabled = false;
        showError('ese email no me cuadra. ¿lo revisas?');
        input.focus();
        return;
      }
      error.hidden = true;
      button.disabled = true;
      form.dataset.sending = '1';
      const label = button.dataset.label || button.textContent;
      button.dataset.label = label;
      button.textContent = '…';

      try {
        if (config.klaviyoKey && config.klaviyoList) {
          const request = fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(config.klaviyoKey)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    custom_source: config.source || 'Chat tu pared',
                    profile: { data: { type: 'profile', attributes: { email } } },
                  },
                  relationships: { list: { data: { type: 'list', id: config.klaviyoList } } },
                },
              }),
            }
          );
          const response = await Promise.race([request, wait(2500).then(() => 'slow')]);
          if (response !== 'slow' && !response.ok) throw new Error(`Klaviyo ${response.status}`);
        }

        // Copia en Shopify, igual que el popup del 10 %
        const body = new URLSearchParams(new FormData(form));
        fetch(form.action || '/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        }).catch(() => {});

        try {
          window.localStorage.setItem('ah_suscrito', '1');
        } catch (e) {
          /* sin almacenamiento */
        }
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', { $email: email }]);
        window._learnq.push(['track', 'Subscribed to Newsletter']);
        if (window.fbq) {
          try {
            window.fbq('track', 'Lead', { content_name: 'Chat tu pared' });
          } catch (e) {
            /* pixel no disponible */
          }
        }

        const masked = email.replace(/^(.).*(@.*)$/, '$1•••$2');
        const out = textBubble('out', masked);
        wrap.replaceWith(out);
        const template = form.querySelector('template[data-success]');
        let last = out;
        for (const p of template ? [...template.content.children] : []) {
          last = await sayAfter(last, bubble('in', p.innerHTML));
        }
      } catch (err) {
        console.error('[ARTYHC] chat tu pared: no se pudo suscribir', err);
        showError('no he podido apuntarte. inténtalo otra vez en un momento.');
        delete form.dataset.sending;
        button.disabled = false;
        button.textContent = label;
      }
    });
  }

  setupPhoto(box) {
    const input = box.querySelector('input[type="file"]');
    const linksTemplate = box.querySelector('template[data-links]');
    /** @type {Element[]} */
    let added = [];
    let objectUrl = '';

    const reset = () => {
      added.forEach((el) => el.remove());
      added = [];
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = '';
      input.value = '';
      box.hidden = false;
    };

    const push = (el) => {
      added.push(el);
      return el;
    };

    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      objectUrl = URL.createObjectURL(file);
      box.hidden = true;

      const image = new Image();
      image.src = objectUrl;
      const decoded = await image.decode().then(
        () => true,
        () => false
      );
      const px = decoded ? Math.max(image.naturalWidth, image.naturalHeight) : 0;

      const thumb = document.createElement('img');
      thumb.className = 'pared__thumb';
      thumb.alt = 'tu foto';
      thumb.src = objectUrl;
      const out = push(bubble('out', thumb));
      if (!decoded) out.textContent = file.name;
      box.after(out);

      let last = await sayAfter(out, push(textBubble('in', box.dataset.received || 'recibida.')));

      if (!decoded) {
        last = await sayAfter(
          last,
          push(
            textBubble(
              'in',
              'este formato no lo puedo abrir aquí (pasa con algunas fotos de iPhone). no pasa nada: al diseñar tu lienzo puedes subirla igual.'
            )
          )
        );
      } else {
        last = await sayAfter(last, push(textBubble('in', resolutionMessage(px))));
        if (box.dataset.room) {
          last = await sayAfter(last, push(textBubble('in', box.dataset.preview || 'así quedaría:')));
          const mock = document.createElement('div');
          mock.className = 'pared__mock';
          mock.style.cssText = box.getAttribute('style') || '';
          const room = document.createElement('img');
          room.className = 'pared__room';
          room.alt = 'salón con un lienzo sobre el sofá';
          room.src = box.dataset.room;
          const art = document.createElement('img');
          art.className = 'pared__art';
          art.alt = 'tu foto convertida en lienzo';
          art.src = objectUrl;
          mock.append(room, art);
          const pic = bubble('in', mock);
          pic.classList.add('pared__pic');
          last = await sayAfter(last, push(pic));
        }
      }

      if (box.dataset.final) {
        last = await sayAfter(last, push(textBubble('in', box.dataset.final, box.dataset.finalFrom)));
      }

      if (linksTemplate) {
        const links = document.createElement('div');
        links.className = 'pared__links pared__new';
        links.append(linksTemplate.content.cloneNode(true));
        links.querySelector('[data-again]')?.addEventListener('click', reset);
        last.after(push(links));
      }
    });
  }
}

/** Mensaje de calidad según el lado largo de la foto (≈ 75 ppp a tamaño final). */
function resolutionMessage(px) {
  const steps = [
    [4400, '100×150'],
    [3500, '80×120'],
    [2900, '70×100'],
    [2000, '50×70'],
    [1200, '30×40'],
  ];
  const fit = steps.find(([min]) => px >= min);
  if (!fit) {
    return `ojo: solo tiene ${px} px. para un lienzo pequeño vale, para algo más grande mándanos el original del móvil, no la de whatsapp.`;
  }
  if (fit[1] === '100×150') return `tiene ${px} px. calidad de sobra, hasta la medida más grande.`;
  return `tiene ${px} px. se verá nítida hasta unos ${fit[1]} cm. más grande, mejor con el original del móvil. igualmente la revisamos a mano antes de imprimir.`;
}

/* ------------------------------------------------------------------ */
/* <pared-medida>                                                      */
/* ------------------------------------------------------------------ */

const GAP_CM = 3;

/**
 * Ancho aproximado (cm) del conjunto colgado a partir del texto de la medida.
 * "40x60cm" · "40x60cmx3" · "30cmx60cmx4" · "20x35cmx2 20x45cmx2 20x55cmx1" · "50X70CMx2 (Conjunto de 2 piezas)"
 * @returns {{width:number, pieces:number}|null}
 */
function parseSize(raw, panoramic) {
  const text = String(raw)
    .toLowerCase()
    .replace(/×/g, 'x')
    .replace(/\s*x\s*/g, 'x')
    .replace(/\s+cm/g, 'cm');
  const re = /(\d+)(?:cm)?x(\d+)(?:cm)?(?:x(\d+)(?!\d*(?:cm)?x\d))?/g;
  const parts = [];
  let m;
  while ((m = re.exec(text))) {
    parts.push({ a: Number(m[1]), b: Number(m[2]), n: m[3] ? Number(m[3]) : 1 });
  }
  if (!parts.length) return null;

  if (parts.length > 1) {
    const pieces = parts.reduce((s, p) => s + p.n, 0);
    const width = parts.reduce((s, p) => s + p.a * p.n, 0) + GAP_CM * (pieces - 1);
    return { width, pieces };
  }

  const { a, b, n } = parts[0];
  if (n === 4 && a === b) return { width: a * 2 + GAP_CM, pieces: 4 }; // 2×2
  if (n > 1) return { width: a * n + GAP_CM * (n - 1), pieces: n };
  const wide = panoramic || Math.max(a, b) / Math.min(a, b) >= 2.5;
  return { width: wide ? Math.max(a, b) : Math.min(a, b), pieces: 1 };
}

const QUESTIONS = {
  room: {
    q: '¿dónde me vas a colgar?',
    a: [
      ['salón', 'salon'],
      ['dormitorio', 'dorm'],
      ['oficina o pasillo', 'ofi'],
    ],
  },
  salon: {
    q: '¿qué hay debajo de mí?',
    a: [
      ['un sofá', 'sofa'],
      ['un mueble o la tele', 'mueble'],
      ['nada, pared libre', 'libre'],
    ],
  },
  ofi: {
    q: '¿encima de qué?',
    a: [
      ['un escritorio o mueble', 'mueble'],
      ['nada, pared libre', 'libre'],
    ],
  },
  dorm: {
    q: '¿encima de la cama? ¿de cuánto es?',
    a: [
      ['de 90', 95],
      ['de 135 o 150', 115],
      ['de 160 o más', 130],
    ],
  },
  sofa: {
    q: '¿cuánto mide el sofá de largo, más o menos?',
    a: [
      ['menos de 180 cm', 105],
      ['de 180 a 220 cm', 130],
      ['más de 220 cm', 155],
    ],
  },
  mueble: {
    q: '¿y de ancho?',
    a: [
      ['menos de 100 cm', 60],
      ['de 100 a 160 cm', 85],
      ['más de 160 cm', 110],
    ],
  },
  libre: {
    q: '¿cómo de grande es la pared?',
    a: [
      ['pequeña, menos de 1,5 m', 55],
      ['mediana, 1,5 a 3 m', 90],
      ['grande, más de 3 m', 130],
    ],
  },
};

class ParedMedida extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.button = this.querySelector('.pared-medida__ask');
    this.panel = this.querySelector('.pared-medida__chat');
    this.log = this.querySelector('.pared__log');
    try {
      this.data = JSON.parse(this.querySelector('script[data-pared-data]')?.textContent || '{}');
    } catch (e) {
      this.data = {};
    }
    this.sizes = (this.data.values || [])
      .map((v, index) => ({ ...v, index, size: parseSize(v.v, this.data.panoramic) }))
      .filter((v) => v.size);
    if (!this.sizes.length || !this.button) {
      this.hidden = true;
      return;
    }
    this.run = 0;
    this.button.addEventListener('click', () => {
      const open = this.panel.hidden;
      this.panel.hidden = !open;
      this.button.setAttribute('aria-expanded', String(open));
      if (open && !this.log.childElementCount) this.start();
    });
  }

  disconnectedCallback() {
    this.run++;
  }

  async say(run, text) {
    if (run !== this.run) return false;
    const dots = typingDots();
    dots.classList.add('is-on');
    this.log.append(dots);
    await beat(typingTime(text));
    if (run !== this.run) return false;
    const node = typeof text === 'string' ? bubble('in', text) : text;
    dots.replaceWith(node);
    await beat(220);
    return run === this.run;
  }

  ask(run, options) {
    return new Promise((resolve) => {
      if (run !== this.run) return resolve(null);
      const box = document.createElement('div');
      box.className = 'pared__opts pared__new';
      for (const [label, value] of options) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pared__opt';
        b.textContent = label;
        b.addEventListener('click', () => {
          box.replaceWith(textBubble('out', label));
          resolve(run === this.run ? value : null);
        });
        box.append(b);
      }
      this.log.append(box);
      box.querySelector('button')?.focus({ preventScroll: true });
    });
  }

  async start() {
    const run = ++this.run;
    this.log.innerHTML = '';
    for (const line of this.data.intro || []) {
      if (!(await this.say(run, escapeHtml(line)))) return;
    }
    if (!(await this.say(run, 'tres preguntas y lo tenemos.'))) return;

    if (!(await this.say(run, QUESTIONS.room.q))) return;
    const room = await this.ask(run, QUESTIONS.room.a);
    if (room == null) return;

    let step = room;
    let target = null;
    while (target == null) {
      const q = QUESTIONS[step];
      if (!(await this.say(run, q.q))) return;
      const answer = await this.ask(run, q.a);
      if (answer == null) return;
      if (typeof answer === 'number') target = answer;
      else step = answer;
    }

    if (!(await this.say(run, 'vale. haciendo cuentas… 📐'))) return;
    const pick = this.recommend(target);
    const s = pick.size;
    const label = prettySize(pick.v);
    const total = s.pieces > 1 ? ` son ${s.pieces} piezas: unos ${Math.round(s.width)} cm de ancho en total.` : '';
    if (!(await this.say(run, `te recomiendo <b>${escapeHtml(label)}</b>.${total}`))) return;

    const ratio = s.width / target;
    let why = 'así ocupa unos dos tercios del espacio, que es lo que queda bien. ni sello ni pancarta.';
    if (ratio < 0.6) {
      why = 'es la más grande de este diseño. para llenar esa pared del todo, mira también los de 3 o 5 piezas.';
    } else if (ratio < 0.85) {
      why = 'es la más grande de este diseño y queda proporcionada. si quieres que llene más, mira los de 3 piezas.';
    }
    if (!(await this.say(run, why))) return;

    const selected = this.select(pick);
    if (selected) {
      if (!(await this.say(run, 'ya te la he marcado arriba. 👆'))) return;
    }

    const next = await this.ask(run, [
      [`añadir ${label} al carrito`, 'add'],
      ['volver a empezar', 'again'],
    ]);
    if (next === 'again') this.start();
    if (next === 'add') {
      const added = await this.addToCart();
      if (!added) await this.say(run, 'esa medida no está disponible ahora mismo. elige otra arriba y te espero.');
    }
  }

  /** La medida más grande que no se pasa del objetivo (con un 15 % de margen), mejor si hay stock. */
  recommend(target) {
    const pool = this.sizes.some((s) => s.ok) ? this.sizes.filter((s) => s.ok) : this.sizes;
    const sorted = [...pool].sort((x, y) => x.size.width - y.size.width);
    const fitting = sorted.filter((s) => s.size.width <= target * 1.15);
    return fitting.length ? fitting[fitting.length - 1] : sorted[0];
  }

  /** Marca la medida en el selector de variantes del tema. */
  select(pick) {
    const scope = this.closest('.shopify-section') || document;
    const id = `${this.data.position}-${pick.index}`;
    const control = scope.querySelector(`[data-input-id="${id}"]`);
    if (!control) return false;
    if (control instanceof HTMLOptionElement) {
      const select = control.closest('select');
      if (select && select.value !== control.value) {
        select.value = control.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (control instanceof HTMLInputElement && !control.checked) {
      control.click();
    }
    control.closest('label, .variant-option__select-wrapper')?.animate?.(
      [{ boxShadow: '0 0 0 0 transparent' }, { boxShadow: '0 0 0 6px #ffcc66' }, { boxShadow: '0 0 0 0 transparent' }],
      { duration: reduceMotion ? 1 : 1200 }
    );
    return true;
  }

  async addToCart() {
    const scope = this.closest('.shopify-section') || document;
    // El tema desactiva el botón mientras actualiza la variante: esperamos a que vuelva (máx. 3 s)
    for (let i = 0; i < 30; i++) {
      const add = scope.querySelector('product-form-component button[name="add"]');
      if (add && !add.disabled) {
        add.click();
        return true;
      }
      await wait(100);
    }
    return false;
  }
}

/** "100X150CM" → "100×150 cm" · "20x30cmx2 30x40cmx1" → "20×30 cm (x2) 30×40 cm (x1)" */
function prettySize(value) {
  return String(value)
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(\d+)\s*(?:cm)?\s*x\s*(\d+)\s*(?:cm)?(?:\s*x\s*(\d+))?/g, (m, a, b, n) => `${a}×${b} cm${n ? ` (x${n})` : ''}`);
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
}

/* ------------------------------------------------------------------ */
/* <pared-toasts>                                                      */
/* ------------------------------------------------------------------ */

class ParedToasts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.lines = [this.dataset.l1, this.dataset.l2, this.dataset.l3].filter(Boolean);
    if (!this.lines.length) return;
    this.abort = new AbortController();
    document.addEventListener('cart:update', (event) => this.onCart(event), { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.timer);
  }

  onCart(event) {
    const data = event.detail?.data;
    if (!data || data.didError || data.source !== 'product-form-component') return;
    const count = Number(session.get('pared-adds') || 0) + 1;
    session.set('pared-adds', String(count));
    this.show(this.lines[Math.min(count, this.lines.length) - 1].split('|'));
  }

  show(parts) {
    clearTimeout(this.timer);
    this.innerHTML = '';
    const host = document.querySelector('dialog[open]') || document.body;
    if (this.parentElement !== host) host.append(this);
    parts.forEach((text, i) => {
      setTimeout(
        () => {
          const toast = document.createElement('div');
          toast.className = 'pared-toast pared__new';
          toast.innerHTML = '<span class="pared__av" aria-hidden="true">🧱</span>';
          toast.append(textBubble('in', text.trim(), 'tu pared'));
          toast.addEventListener('click', () => this.hide());
          this.append(toast);
        },
        reduceMotion ? 0 : i * 900
      );
    });
    this.timer = setTimeout(() => this.hide(), parts.length * 900 + 3800);
  }

  hide() {
    clearTimeout(this.timer);
    this.querySelectorAll('.pared-toast').forEach((t) => t.classList.add('is-out'));
    setTimeout(() => (this.innerHTML = ''), 400);
  }
}

/* ------------------------------------------------------------------ */
/* <pared-upsell> · "completa tu pared" dentro del carrito             */
/* ------------------------------------------------------------------ */

class ParedUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.addEventListener('submit', (event) => this.add(event));
  }

  async add(event) {
    const form = event.target.closest('form[data-pared-upsell]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const error = this.querySelector('.pared-cart__card-err');
    const cartItems = this.closest('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId;
    const id = Number(new FormData(form).get('id'));
    if (!id || button.disabled) return;

    button.disabled = true;
    const label = button.textContent;
    button.textContent = 'añadiendo…';
    if (error) error.hidden = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id, quantity: 1 }],
          ...(sectionId ? { sections: sectionId, sections_url: window.location.pathname } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'No se pudo añadir');

      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'pared-upsell',
            data: { source: 'pared-upsell', itemCount: cart.item_count, sections: data.sections },
          },
        })
      );
    } catch (err) {
      console.error('[ARTYHC] carrito: no se pudo añadir el lienzo recomendado', err);
      if (error) {
        error.textContent = 'No se ha podido añadir. Prueba otra medida o inténtalo de nuevo.';
        error.hidden = false;
      }
      button.disabled = false;
      button.textContent = label;
    }
  }
}

if (!customElements.get('pared-chat')) customElements.define('pared-chat', ParedChat);
if (!customElements.get('pared-upsell')) customElements.define('pared-upsell', ParedUpsell);
if (!customElements.get('pared-medida')) customElements.define('pared-medida', ParedMedida);
if (!customElements.get('pared-toasts')) customElements.define('pared-toasts', ParedToasts);
